import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {TranscoderService} from '../../services/transcoder.service';
import {handleFetchListPage} from '../../helpers/search.helper';
import {setFormStatus, setFormValidationErrors} from '../../helpers/form.helper';
import {FormStatus} from '../../enums/file-status.enum';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {NavService} from '../../services/nav.service';
import {TranscoderActions} from './transcoder.actions';
import {SearchList} from '../../models/search-list.model';
import {FormState} from '../../models/form-state.model';
import {Transcoder, TranscoderSimple} from '../../models/transcoder.model';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {SearchOrder} from '../../enums/search-order.enum';

interface TranscoderStateModel {
    form: FormState;
    list: SearchList<{}>;
    activeTranscoder: Transcoder;
    fieldList: TranscoderSimple[];
}

const STATE_NAME = 'transcoder';

@State<TranscoderStateModel>({
    name: STATE_NAME,
    defaults: {
        form: new FormState(),
        list: new SearchList<{}>({
            filters: new FormState(),
            orderByField: 'index',
            orderDirection: SearchOrder.ASC
        }),
        activeTranscoder: null,
        fieldList: [],
    }
})
@Injectable()
export class TranscoderState {
    constructor(
        private transcoderService: TranscoderService,
        private navService: NavService
    ) {}

    @Selector()
    static transcoderList(state) {
        return state.list;
    }

    @Selector()
    static formErrors(state) {
        return state.form.validationErrors;
    }

    @Selector()
    static activeTranscoder(state) {
        return state.activeTranscoder;
    }

    @Selector()
    static fieldList(state) {
        return state.fieldList;
    }

    @Action(TranscoderActions.GetListPage)
    getListPage(ctx: StateContext<TranscoderStateModel>, action: TranscoderActions.GetListPage) {
        return handleFetchListPage(ctx, action.params, (props) => this.transcoderService.fetchList(props));
    }

    @Action(TranscoderActions.GetById)
    getDocument(ctx: StateContext<TranscoderStateModel>, {id}: TranscoderActions.GetById) {
        return this.transcoderService.fetchById(id).pipe(
            tap((transcoderItem) => {
                ctx.patchState({
                    activeTranscoder: new Transcoder(transcoderItem)
                });
            })
        );
    }

    @Action(TranscoderActions.Add)
    addTranscoder(ctx: StateContext<TranscoderStateModel>, {data}: TranscoderActions.Add) {
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.transcoderService.add(data).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.TRANSCODER_CONFIGURATION),
            ({error}) => ctx.dispatch(new TranscoderActions.FormFailure(error))
        );
    }

    @Action(TranscoderActions.FormFailure)
    addDocumentFailure(ctx: StateContext<TranscoderStateModel>, {validationErrors = []}: TranscoderActions.FormFailure) {
        setFormValidationErrors(ctx, validationErrors, STATE_NAME);
    }

    @Action(TranscoderActions.Delete)
    deleteBrand(ctx: StateContext<TranscoderStateModel>, {item}: TranscoderActions.Delete) {
        return this.transcoderService.delete(item.id).subscribe(
            () => ctx.dispatch(new TranscoderActions.GetListPage())
        );
    }

    @Action(TranscoderActions.Update)
    updateTranscoder(ctx: StateContext<TranscoderStateModel>, {data}: TranscoderActions.Update) {
        setFormStatus(ctx, FormStatus.PENDING, STATE_NAME);
        return this.transcoderService.update(data).subscribe(
            () => this.navService.navigateByCode(NavItemCodes.TRANSCODER_CONFIGURATION),
            ({error}) => ctx.dispatch(new TranscoderActions.FormFailure(error))
        );
    }

    @Action(TranscoderActions.SearchIndex)
    searchIndex(ctx: StateContext<TranscoderStateModel>, {index}: TranscoderActions.SearchIndex) {
        return this.transcoderService.searchIndex(index).pipe(
            switchMap((fieldList) => ctx.dispatch(new TranscoderActions.SetFieldList(fieldList))),
            catchError(({error}) => ctx.dispatch(new TranscoderActions.FormFailure(error)))
        );
    }

    @Action(TranscoderActions.SetFieldList)
    setFieldList(ctx: StateContext<TranscoderStateModel>, {fieldList}: TranscoderActions.SetFieldList) {
        ctx.patchState({
            fieldList: Array.isArray(fieldList) ? fieldList : []
        });
    }
}

